import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { findOption, toGroupedOptions, toOptionData } from './helpers';

// Browser-native rendering of the same API: a real <select> (single or
// multiple) fed from the normalized options, for contexts that need native
// form semantics (form posts, mobile pickers) instead of the custom menu.
const SelectDropdownNative = ({
    options,
    value,
    onChange,
    multiple = false,
    disabled = false,
    required = false,
    placeholder,
    allowClear = false,
    id,
    name,
    onFocus,
    onBlur,
    selectRef,
    ariaInvalid,
    ariaDescribedBy,
}) => {
    const grouped = useMemo(() => toGroupedOptions(options), [options]);

    const handleChange = (event) => {
        // A <select> always yields strings; the custom-menu path emits the
        // option's own value. Resolve back through the options so both modes
        // return the same type for the same pick.
        if (multiple) {
            const selectedValues = Array.from(event.target.selectedOptions, (node) => node.value);
            const picked = selectedValues.map((selected) => findOption(options, selected));
            const data = picked.map((option, index) =>
                option
                    ? toOptionData(option)
                    : {
                          id: selectedValues[index],
                          text: selectedValues[index],
                          value: selectedValues[index],
                          label: selectedValues[index],
                      },
            );
            onChange(
                picked.map((option, index) => (option ? option.value : selectedValues[index])),
                { data },
            );
            return;
        }
        const selected = event.target.value;
        if (selected === '') {
            onChange(null, { data: null });
            return;
        }
        const option = findOption(options, selected);
        onChange(option ? option.value : selected, { data: option ? toOptionData(option) : null });
    };

    const renderOption = (option) => (
        <option key={`${option.group ?? ''}-${option.value}`} value={option.value} disabled={option.disabled}>
            {option.label}
        </option>
    );

    return (
        <select
            ref={selectRef}
            id={id}
            name={name}
            className="ds-select-dropdown__native"
            multiple={multiple}
            disabled={disabled}
            required={required}
            value={multiple ? (value || []).map(String) : value ?? ''}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
        >
            {!multiple && placeholder !== undefined && placeholder !== null && (
                <option value="" disabled={!allowClear} hidden={!allowClear}>
                    {placeholder}
                </option>
            )}
            {grouped.map((entry, index) =>
                entry.options ? (
                    <optgroup key={`group-${index}-${entry.label}`} label={entry.label}>
                        {entry.options.map(renderOption)}
                    </optgroup>
                ) : (
                    renderOption(entry)
                ),
            )}
        </select>
    );
};

SelectDropdownNative.propTypes = {
    /** Normalized options (from helpers.normalizeOptions) */
    options: PropTypes.arrayOf(PropTypes.object).isRequired,
    /** Selected value (single) or values (multiple) */
    value: PropTypes.any,
    /** Called with (value, { data }) like the custom-menu mode */
    onChange: PropTypes.func.isRequired,
    /** Renders a native multi-select listbox */
    multiple: PropTypes.bool,
    /** Disables the control */
    disabled: PropTypes.bool,
    /** Marks the native control required for form validation */
    required: PropTypes.bool,
    /** Empty-option text for single selects */
    placeholder: PropTypes.string,
    /** Keeps the empty option selectable so the value can be cleared */
    allowClear: PropTypes.bool,
    /** DOM id forwarded to the select */
    id: PropTypes.string,
    /** Form field name */
    name: PropTypes.string,
    /** Focus/blur handlers forwarded to the select */
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    /** Ref to the underlying select element */
    selectRef: PropTypes.object,
    /** Marks the control invalid to assistive technology */
    ariaInvalid: PropTypes.bool,
    /** Id of the error or helper text that explains the control */
    ariaDescribedBy: PropTypes.string,
};

export default SelectDropdownNative;
